/**
 * Pure transforms: reporting-service bot payloads → Analytics panel view-model.
 */

import { formatDurationSeconds } from "@/utils/dateRangeUtc";

const SENTIMENT_LABELS = {
  happy_satisfied: { label: "Happy / satisfied", color: "#10b981" },
  neutral: { label: "Neutral", color: "#64748b" },
  frustrated_angry_disappointed: { label: "Frustrated / angry", color: "#e11d48" },
  general_query: { label: "General query", color: "#8b5cf6" },
};

const HANDOFF_COLORS = ["#0d9488", "#f59e0b", "#e11d48", "#8b5cf6", "#64748b"];

export class ReportingBotDeriver {
  _formatPctChange(value) {
    const n = Number(value);
    const x = Number.isFinite(n) ? n : 0;
    return `${x >= 0 ? "+" : ""}${x.toFixed(1)}%`;
  }

  _formatCsatPct(score) {
    const n = Number(score);
    if (!Number.isFinite(n) || n <= 0) return "—";
    return `${Math.min(100, Math.round((n / 5) * 1000) / 10)}%`;
  }

  /**
   * @param {unknown} payload
   * @param {string} label
   */
  assertPayload(payload, label) {
    if (!payload || typeof payload !== "object") {
      throw new Error(`${label}: invalid response`);
    }
    const o = /** @type {Record<string, unknown>} */ (payload);
    if (o.error) throw new Error(String(o.error));
  }

  /**
   * @param {Record<string, number>} dataObj
   */
  buildVolumeDays(dataObj) {
    if (!dataObj || typeof dataObj !== "object") return [];
    const entries = Object.entries(dataObj)
      .map(([date, count]) => ({ date, sessions: Number(count) || 0 }))
      .sort((a, b) => String(a.date).localeCompare(String(b.date)));
    return entries.map((e) => {
      const d = new Date(`${e.date}T12:00:00.000Z`);
      const day = d.toLocaleDateString("en-US", { weekday: "short", timeZone: "UTC" });
      return { day, date: e.date, sessions: e.sessions };
    });
  }

  volumeMaxFromDays(days) {
    if (!Array.isArray(days) || !days.length) return 1;
    let m = 1;
    for (const d of days) {
      const s = Number(d.sessions) || 0;
      if (s > m) m = s;
    }
    return m;
  }

  /**
   * @param {Record<string, unknown>} raw
   */
  buildKpis(raw) {
    const daily = /** @type {Record<string, unknown>} */ (raw.dailyChats || {});
    const completion = /** @type {Record<string, unknown>} */ (raw.completion || {});
    const avgTime = /** @type {Record<string, unknown>} */ (raw.avgChatTime || {});
    const csat = /** @type {Record<string, unknown>} */ (raw.csat || {});
    const escalation = /** @type {Record<string, unknown>} */ (raw.escalation || {});
    const dropoff = /** @type {Record<string, unknown>} */ (raw.dropoff || {});

    const avgMin = Number(avgTime.avg_time_minutes);
    const durationLabel =
      Number.isFinite(avgMin) && avgMin > 0 ? `${avgMin.toFixed(1)} min` : formatDurationSeconds(Number(avgTime.avg_time_seconds));

    return [
      {
        id: "bot-total",
        label: "Bot conversations",
        value: String(daily.total ?? "—"),
        trend: this._formatPctChange(daily.change),
        trendUp: (Number(daily.change) || 0) >= 0,
        icon: "ri-robot-2-line",
        color: "#7c3aed",
      },
      {
        id: "bot-completion",
        label: "Completion rate",
        value: `${Number(completion.rate ?? 0).toFixed(1)}%`,
        trend: this._formatPctChange(completion.change),
        trendUp: (Number(completion.change) || 0) >= 0,
        icon: "ri-checkbox-circle-line",
        color: "#10b981",
      },
      {
        id: "bot-duration",
        label: "Avg session duration",
        value: durationLabel || "—",
        trend: this._formatPctChange(avgTime.change),
        trendUp: (Number(avgTime.change) || 0) <= 0,
        icon: "ri-time-line",
        color: "#f59e0b",
      },
      {
        id: "bot-csat",
        label: "Customer satisfaction",
        value: this._formatCsatPct(csat.score),
        trend: this._formatPctChange(csat.change),
        trendUp: (Number(csat.change) || 0) >= 0,
        icon: "ri-emotion-happy-line",
        color: "#ec4899",
      },
    ];
  }

  /**
   * @param {Record<string, number>} byReason
   */
  buildHandoffReasonShare(byReason) {
    if (!byReason || typeof byReason !== "object") return [];
    const entries = Object.entries(byReason)
      .map(([label, count]) => ({ label, count: Number(count) || 0 }))
      .filter((e) => e.count > 0)
      .sort((a, b) => b.count - a.count);
    const total = entries.reduce((s, e) => s + e.count, 0);
    if (total <= 0) return [];
    return entries.map((e, i) => ({
      label: e.label,
      count: e.count,
      value: Math.round((e.count / total) * 1000) / 10,
      color: HANDOFF_COLORS[i % HANDOFF_COLORS.length],
    }));
  }

  /**
   * @param {Record<string, unknown>} sentimentPayload
   */
  buildSentimentShare(sentimentPayload) {
    const data =
      sentimentPayload?.data && typeof sentimentPayload.data === "object"
        ? sentimentPayload.data
        : sentimentPayload;
    const counts =
      data?.sentiment_counts && typeof data.sentiment_counts === "object" ? data.sentiment_counts : {};
    const entries = Object.entries(counts)
      .map(([key, count]) => ({
        key,
        label: SENTIMENT_LABELS[key]?.label ?? key.replace(/_/g, " "),
        count: Number(count) || 0,
        color: SENTIMENT_LABELS[key]?.color ?? "#94a3b8",
      }))
      .filter((e) => e.count > 0);
    const total = entries.reduce((s, e) => s + e.count, 0);
    if (total <= 0) return [];
    return entries.map((e) => ({
      label: e.label,
      count: e.count,
      value: Math.round((e.count / total) * 1000) / 10,
      color: e.color,
    }));
  }

  /**
   * @param {Record<string, unknown>} thumbs
   */
  buildThumbsShare(thumbs) {
    const pos = Number(thumbs.positive_feedback) || 0;
    const neg = Number(thumbs.negative_feedback) || 0;
    const total = Number(thumbs.total_feedback) || pos + neg;
    if (total <= 0) return [];
    return [
      {
        label: "Thumbs up",
        count: pos,
        value: Math.round((pos / total) * 1000) / 10,
        color: "#10b981",
      },
      {
        label: "Thumbs down",
        count: neg,
        value: Math.round((neg / total) * 1000) / 10,
        color: "#e11d48",
      },
    ];
  }

  /**
   * @param {Record<string, unknown>} distribution
   */
  buildRatingRows(distribution) {
    const keys = ["5", "4", "3", "2", "1"];
    const rows = keys.map((k) => ({
      stars: k,
      count: Number(distribution[k]) || 0,
    }));
    const max = Math.max(1, ...rows.map((r) => r.count));
    return rows.map((r) => ({
      ...r,
      pctWidth: Math.round((r.count / max) * 100),
    }));
  }

  /**
   * @param {unknown} topicsPayload
   */
  buildTrendingTopicsList(topicsPayload) {
    const list = Array.isArray(topicsPayload?.trending_topics)
      ? topicsPayload.trending_topics
      : Array.isArray(topicsPayload?.trending_words)
        ? topicsPayload.trending_words
        : [];
    return list.map((t, i) => {
      if (typeof t === "string") return { rank: i + 1, word: t, count: null };
      const o = /** @type {Record<string, unknown>} */ (t);
      return {
        rank: i + 1,
        word: String(o.word ?? o.topic ?? o.label ?? "—"),
        count: o.count != null ? Number(o.count) : o.frequency != null ? Number(o.frequency) : null,
      };
    });
  }

  /**
   * @param {Record<string, unknown>} raw
   */
  buildViewModel(raw) {
    const daily = /** @type {Record<string, unknown>} */ (raw.dailyChats || {});
    const escalation = /** @type {Record<string, unknown>} */ (raw.escalation || {});
    const volumeDays = this.buildVolumeDays(
      daily.data && typeof daily.data === "object" ? /** @type {Record<string, number>} */ (daily.data) : {},
    );

    return {
      kpis: this.buildKpis(raw),
      volumeWeek: volumeDays,
      volumeMax: this.volumeMaxFromDays(volumeDays),
      volumeEmpty: volumeDays.length === 0,
      handoffShare: this.buildHandoffReasonShare(
        escalation.by_reason && typeof escalation.by_reason === "object"
          ? /** @type {Record<string, number>} */ (escalation.by_reason)
          : {},
      ),
      sentimentShare: this.buildSentimentShare(raw.sentiment),
      thumbsShare: this.buildThumbsShare(/** @type {Record<string, unknown>} */ (raw.thumbs || {})),
      ratingRows: this.buildRatingRows(/** @type {Record<string, unknown>} */ (raw.rating || {})),
      trendingTopics: this.buildTrendingTopicsList(raw.trending),
      meta: {
        sessionTotal: Number(daily.total) || 0,
        uniqueUsers: Number(raw.unique?.unique_conversations) || 0,
        returningUsers: Number(raw.returning?.returning_users) || 0,
        periodStart: raw.periodStart,
        periodEnd: raw.periodEnd,
      },
    };
  }
}

export const reportingBotDeriver = new ReportingBotDeriver();
